import React, { useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiCamera, PiArrowUpRight } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";
import { getImageUrl } from "@/utils/image-url";
import logger from "@/utils/logger";

export default function UniversityAbout({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const c = content || {};
  const primaryColor = c.colorPalette?.primary || "#7A1520";
  const fontFamily = c.fontFamily || "'Georgia', 'Times New Roman', serif";

  const img1InputRef = useRef<HTMLInputElement>(null);
  const img2InputRef = useRef<HTMLInputElement>(null);

  const title = c.title || "ABOUT OUR UNIVERSITY INSTACKS";
  const description = c.description || "At University Instacks, we believe in the transformative power of education and the boundless potential within every individual. Established in 1971, we have been dedicated to fostering intellectual curiosity, academic excellence, and a vibrant campus community.";
  const establishedText = c.establishedText || "EST. 1971";
  const circleText = c.circleText || "* Explore Future *Instacks University * Estd. 1971 ";
  const ctaText = c.ctaText || "University Overview";
  const ctaLink = c.ctaLink || "#";

  const rawImageUrl1 = c.imageUrl || "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=600&h=800&fit=crop";
  const rawImageUrl2 = c.imageUrl2 || "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&h=600&fit=crop";
  const imageUrl1 = getImageUrl(rawImageUrl1) || rawImageUrl1;
  const imageUrl2 = getImageUrl(rawImageUrl2) || rawImageUrl2;

  // Stats
  const stat1Value = c.stat1Value || "20,000";
  const stat1Label = c.stat1Label || "Undergraduate & Graduate Students";
  const stat2Value = c.stat2Value || "16,214";
  const stat2Label = c.stat2Label || "Instacks University Faculty & Staff";
  const stat3Value = c.stat3Value || "300K";
  const stat3Label = c.stat3Label || "Instacks University Alumni Worldwide";
  const stat4Value = c.stat4Value || "20,000";
  const stat4Label = c.stat4Label || "Undergraduate & Graduate Students";

  const handleImageUpload = async (file: File, fieldKey: "imageUrl" | "imageUrl2") => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const uploadPromise = homepageApi.uploadImage(file);
    toast.promise(uploadPromise, {
      loading: "Uploading image...",
      success: "Image uploaded successfully!",
      error: "Failed to upload image",
    });

    try {
      const response = await uploadPromise;
      if (response.success && response.data?.url) {
        onContentChange?.({
          ...c,
          [fieldKey]: response.data.url
        });
      } else {
        toast.error(response.message || "Upload failed");
      }
    } catch (err) {
      logger.error(err);
      toast.error("Upload failed");
    }
  };

  return (
    <div
      className={`bg-white px-8 py-20 lg:py-24 relative overflow-hidden ${
        isSelected ? "ring-2 ring-blue-500 z-10" : ""
      }`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto flex flex-col gap-16 lg:gap-20">
        
        {/* Top Content Row */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          
          {/* Staggered Images Left Side */}
          <div className="w-full lg:w-[48%] relative min-h-[380px] sm:min-h-[480px] flex-shrink-0">
            {/* Top-Left Image */}
            <div
              onClick={(e) => {
                if (isSelected) {
                  e.stopPropagation();
                  img1InputRef.current?.click();
                }
              }}
              className={`w-[65%] aspect-[3/4] rounded-sm overflow-hidden shadow-lg absolute top-0 left-0 ${
                isSelected ? "group/img cursor-pointer" : ""
              }`}
            >
              <img
                src={imageUrl1}
                alt="University Building"
                className="w-full h-full object-cover"
              />
              {isSelected && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-xs gap-1.5 font-sans font-medium">
                  <PiCamera className="w-6 h-6" />
                  <span>Upload Left Image</span>
                </div>
              )}
              <input
                type="file"
                ref={img1InputRef}
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(file, "imageUrl");
                }}
              />
            </div>

            {/* Bottom-Right Image */}
            <div
              onClick={(e) => {
                if (isSelected) {
                  e.stopPropagation();
                  img2InputRef.current?.click();
                }
              }}
              className={`w-[55%] aspect-square rounded-sm overflow-hidden absolute bottom-[-15px] right-0 z-10 border-[8px] sm:border-[12px] border-white shadow-xl ${
                isSelected ? "group/img cursor-pointer" : ""
              }`}
            >
              <img
                src={imageUrl2}
                alt="University Campus"
                className="w-full h-full object-cover"
              />
              {isSelected && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-xs gap-1.5 font-sans font-medium">
                  <PiCamera className="w-6 h-6" />
                  <span>Upload Right Image</span>
                </div>
              )}
              <input
                type="file"
                ref={img2InputRef}
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(file, "imageUrl2");
                }}
              />
            </div>

            {/* Rotating circle overlay */}
            <div
              className="absolute top-[45%] left-[45%] -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ width: "125px", height: "125px" }}
            >
              <div
                className="relative w-full h-full rounded-full flex items-center justify-center border-2 border-white/30"
                style={{
                  backgroundColor: primaryColor,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                }}
              >
                <svg
                  viewBox="0 0 100 100"
                  className="w-full h-full absolute inset-0"
                  style={{
                    animation: "uniHeroSpin 12s linear infinite",
                  }}
                >
                  <defs>
                    <path
                      id="uni-about-circle-path"
                      d="M50,50 m-40,0a40,40 0 1,1 80,0a40,40 0 1,1 -80,0"
                      fill="none"
                    />
                  </defs>
                  <text
                    fill="rgba(255,255,255,0.85)"
                    fontSize="10.5"
                    fontFamily="'Inter', 'Helvetica', sans-serif"
                    letterSpacing="1.4"
                  >
                    <textPath xlinkHref="#uni-about-circle-path">
                      {circleText}
                    </textPath>
                  </text>
                </svg>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="relative z-10"
                >
                  <line x1="7" y1="17" x2="17" y2="7"></line>
                  <polyline points="7 7 17 7 17 17"></polyline>
                </svg>
              </div>
            </div>
          </div>

          {/* Text Content Right Side */}
          <div className="flex-1 flex flex-col items-start text-left lg:pl-4">
            <h2 className="text-3xl sm:text-4xl font-normal text-gray-900 leading-tight tracking-wide mb-6 uppercase">
              <EditableText
                value={title}
                onChange={(val) => onContentChange?.({ ...c, title: val })}
                as="span"
                placeholder="Title"
              />
            </h2>
            
            <p className="text-gray-500 leading-relaxed mb-8 text-sm sm:text-base font-sans">
              <EditableText
                value={description}
                onChange={(val) => onContentChange?.({ ...c, description: val })}
                as="span"
                placeholder="Description"
              />
            </p>

            {/* Large Hollow/Outline Year */}
            <div 
              className="text-[44px] sm:text-[60px] font-bold leading-none mb-8 tracking-wide select-none"
              style={{
                fontFamily: "'Inter', sans-serif",
                color: "transparent",
                WebkitTextStroke: "1px #CCCCCC"
              }}
            >
              <EditableText
                value={establishedText}
                onChange={(val) => onContentChange?.({ ...c, establishedText: val })}
                as="span"
                placeholder="EST. 1971"
              />
            </div>

            {/* Underlined CTA Link */}
            <a
              href={ctaLink}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold tracking-wide uppercase transition-opacity hover:opacity-85 border-b-2 pb-1 font-sans"
              style={{
                color: primaryColor,
                borderColor: primaryColor,
              }}
            >
              <EditableText
                value={ctaText}
                onChange={(val) => onContentChange?.({ ...c, ctaText: val })}
                as="span"
                placeholder="CTA Link"
              />
              <PiArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Bottom Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 border-t border-gray-100 pt-12">
          {/* Stat 1 */}
          <div className="flex flex-col items-start md:border-r border-gray-100 md:pr-4 last:border-0">
            <span className="text-3xl sm:text-4xl font-normal mb-2" style={{ color: primaryColor }}>
              <EditableText
                value={stat1Value}
                onChange={(val) => onContentChange?.({ ...c, stat1Value: val })}
                as="span"
                placeholder="20,000"
              />
            </span>
            <span className="text-[11px] sm:text-xs text-gray-500 font-sans leading-snug">
              <EditableText
                value={stat1Label}
                onChange={(val) => onContentChange?.({ ...c, stat1Label: val })}
                as="span"
                placeholder="Label"
              />
            </span>
          </div>

          {/* Stat 2 */}
          <div className="flex flex-col items-start md:border-r border-gray-100 md:pr-4 last:border-0">
            <span className="text-3xl sm:text-4xl font-normal mb-2" style={{ color: primaryColor }}>
              <EditableText
                value={stat2Value}
                onChange={(val) => onContentChange?.({ ...c, stat2Value: val })}
                as="span"
                placeholder="16,214"
              />
            </span>
            <span className="text-[11px] sm:text-xs text-gray-500 font-sans leading-snug">
              <EditableText
                value={stat2Label}
                onChange={(val) => onContentChange?.({ ...c, stat2Label: val })}
                as="span"
                placeholder="Label"
              />
            </span>
          </div>

          {/* Stat 3 */}
          <div className="flex flex-col items-start md:border-r border-gray-100 md:pr-4 last:border-0">
            <span className="text-3xl sm:text-4xl font-normal mb-2" style={{ color: primaryColor }}>
              <EditableText
                value={stat3Value}
                onChange={(val) => onContentChange?.({ ...c, stat3Value: val })}
                as="span"
                placeholder="300K"
              />
            </span>
            <span className="text-[11px] sm:text-xs text-gray-500 font-sans leading-snug">
              <EditableText
                value={stat3Label}
                onChange={(val) => onContentChange?.({ ...c, stat3Label: val })}
                as="span"
                placeholder="Label"
              />
            </span>
          </div>

          {/* Stat 4 */}
          <div className="flex flex-col items-start last:border-0">
            <span className="text-3xl sm:text-4xl font-normal mb-2" style={{ color: primaryColor }}>
              <EditableText
                value={stat4Value}
                onChange={(val) => onContentChange?.({ ...c, stat4Value: val })}
                as="span"
                placeholder="20,000"
              />
            </span>
            <span className="text-[11px] sm:text-xs text-gray-500 font-sans leading-snug">
              <EditableText
                value={stat4Label}
                onChange={(val) => onContentChange?.({ ...c, stat4Label: val })}
                as="span"
                placeholder="Label"
              />
            </span>
          </div>
        </div>
      </div>
      
      {/* Keyframe animation styling */}
      <style>{`
        @keyframes uniHeroSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
